// Mesureur de taille (blocks/nose-sizer.liquid) : anneaux, barres et labrets, toutes zones.
// La photo est lue et dessinée dans le navigateur : elle n'est jamais envoyée.

const CLE_MEMOIRE = 'plumeria:mesures';
const DUREE_MEMOIRE = 365 * 24 * 3600 * 1000;
const COTE_MAX_PX = 1600;
const COULEURS = { piece: '#E8B04A', mesure: '#D94F7A' };

// Pour chaque zone : le jeu (mm) ajouté à la mesure prise sur soi, et les deux points à toucher
// selon le bijou. Anneau : du trou au bord qu'il entoure. Barre : d'un trou à l'autre.
const MARGE_PAR_DEFAUT = 2;
const TROU = 'le trou de votre piercing';
const ZONES = {
  narine: { label: 'Narine', marge: 1, anneau: [TROU, 'le bord de la narine, juste en dessous du trou'] },
  septum: { label: 'Septum', anneau: [TROU, 'le bas de la cloison, juste en dessous du trou'] },
  helix: { label: 'Hélix', anneau: [TROU, "le bord extérieur de l'oreille, au plus près du trou"] },
  conch: { label: 'Conch', anneau: [TROU, "le bord extérieur de l'oreille, au plus près du trou"] },
  daith: { label: 'Daith', anneau: [TROU, 'le bord du cartilage, au plus près du trou'] },
  tragus: { label: 'Tragus', anneau: [TROU, 'le bord du tragus, au plus près du trou'] },
  lobe: { label: 'Lobe', anneau: [TROU, 'le bas du lobe, juste en dessous du trou'] },
  oreille: {
    label: 'Oreille',
    anneau: [TROU, "le bord de l'oreille, au plus près du trou"],
    barre: ['le premier trou', 'le second trou'],
  },
  industriel: { label: 'Industriel', barre: ['le premier trou', 'le second trou'] },
  levre: {
    label: 'Lèvre',
    anneau: [TROU, 'le bord de la lèvre, au plus près du trou'],
    barre: ['le trou au-dessus de la lèvre', 'le trou sous la lèvre'],
  },
  arcade: { label: 'Arcade', barre: ['le trou du haut', 'le trou du bas'] },
  nombril: {
    label: 'Nombril',
    anneau: ['le trou du haut', 'le trou du bas, dans le nombril'],
    barre: ['le trou du haut', 'le trou du bas, dans le nombril'],
  },
  teton: { label: 'Téton', barre: ['le premier trou', "le second trou, de l'autre côté"] },
};

// Par type de bijou : le mot de la taille, les mesures plausibles, et les deux points à toucher
// sur le bijou actuel posé à plat à côté de la pièce.
const TYPES = {
  anneau: {
    mot: 'Diamètre',
    accord: '',
    prefixe: 'Ø ',
    min: 1,
    max: 16,
    bijou: ['un bord intérieur de votre anneau', 'le bord intérieur opposé'],
  },
  barre: {
    mot: 'Longueur',
    accord: 'e',
    prefixe: '',
    min: 3,
    max: 45,
    bijou: ['le début de la barre, contre la première boule', 'la fin de la barre, contre la seconde boule'],
  },
  tige: {
    mot: 'Longueur de tige',
    accord: 'e',
    prefixe: '',
    min: 3,
    max: 20,
    bijou: ['le disque plat, à la base de la tige', "le bout de la tige, sous l'embout"],
  },
};

const METHODES = { corps: 'Sur moi', bijou: 'Mon bijou actuel' };
const ETAPE_PHOTO = {
  corps: 'Prenez une photo de près, avec une pièce posée à plat contre la peau, juste à côté du piercing.',
  bijou: "Posez votre bijou actuel à plat sur une table, à côté d'une pièce, et photographiez-les d'au-dessus.",
};

const enMm = (mm) => mm.toLocaleString('fr-FR', { maximumFractionDigits: 1 });
const echapper = (texte) =>
  String(texte).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const ecart = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

class NoseSizer extends HTMLElement {
  connectedCallback() {
    if (this.dialog) return;

    this.dialog = this.querySelector('dialog');
    this.canvas = this.querySelector('[data-canvas]');
    this.ctx = this.canvas.getContext('2d');
    this.champManuel = this.querySelector('[data-manual]');
    this.photo = null;
    this.points = [];
    this.deplacement = null;

    this.type = TYPES[this.dataset.type] ? this.dataset.type : 'anneau';
    // Taille unique : pas de choix à faire, on dit seulement si le bijou convient.
    this.unique = this.dataset.unique === 'true';
    this.tailles = this.dataset.sizes
      .split('|')
      .map((label) => label.trim())
      .filter(Boolean)
      .map((label) => ({ label, mm: parseFloat(label.replace(',', '.')) }))
      .filter((taille) => Number.isFinite(taille.mm))
      .sort((a, b) => a.mm - b.mm);

    // Zones où le bijou se porte et qui se mesurent sur soi pour ce type de bijou.
    // Un labret ne se mesure que sur le bijou actuel : la tige traverse la peau.
    this.zones = (this.dataset.zones || '').split('|').filter((zone) => ZONES[zone]?.[this.type]);
    this.methodes = this.zones.length ? ['corps', 'bijou'] : ['bijou'];
    this.methode = this.methodes[0];
    this.zone = this.zones[0] ?? null;

    this.construireChoix('[data-methodes]', 'methode', this.methodes.map((m) => [m, METHODES[m]]));
    this.construireChoix('[data-zones]', 'zone', this.zones.map((z) => [z, ZONES[z].label]));

    this.querySelector('[data-open]').addEventListener('click', () => this.ouvrir());
    this.querySelector('[data-close]').addEventListener('click', () => this.dialog.close());
    // Clic sur le fond assombri (hors de la fenêtre) : on ferme.
    this.dialog.addEventListener('click', (event) => {
      const cadre = this.dialog.getBoundingClientRect();
      const dedans =
        event.clientX >= cadre.left && event.clientX <= cadre.right && event.clientY >= cadre.top && event.clientY <= cadre.bottom;
      if (event.target === this.dialog && !dedans) this.dialog.close();
    });
    this.querySelector('[data-file]').addEventListener('change', (event) => this.chargerPhoto(event.target.files?.[0]));
    this.querySelector('[data-coin]').addEventListener('change', () => this.mettreAJour());
    this.querySelector('[data-undo]').addEventListener('click', () => {
      this.points.pop();
      this.mettreAJour();
    });
    this.champManuel.addEventListener('input', () => this.afficherConseil(this.distanceManuelle()));

    this.canvas.addEventListener('pointerdown', (event) => this.appui(event));
    this.canvas.addEventListener('pointermove', (event) => this.glisse(event));
    this.canvas.addEventListener('pointerup', () => this.relache());
    this.canvas.addEventListener('pointercancel', () => this.relache());

    this.afficherEtapes();
    this.afficherMemoire();
  }

  construireChoix(selecteur, cle, options) {
    const groupe = this.querySelector(selecteur);
    const nom = `${cle}-${this.dataset.productId}-${Math.random().toString(36).slice(2, 8)}`;
    groupe.hidden = options.length < 2;
    for (const [valeur, libelle] of options) {
      const label = document.createElement('label');
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = nom;
      input.value = valeur;
      input.checked = valeur === this[cle];
      input.addEventListener('change', () => this.changer(cle, valeur));
      const texte = document.createElement('span');
      texte.textContent = libelle;
      label.append(input, texte);
      groupe.append(label);
    }
  }

  changer(cle, valeur) {
    this[cle] = valeur;
    // Une autre méthode demande une autre photo (le piercing ou le bijou) : on repart de zéro.
    if (cle === 'methode') this.reinitialiser();
    this.afficherEtapes();
    this.dessiner();
    const mesure = this.mesurePhoto() ?? this.distanceManuelle();
    if (mesure !== null) {
      this.afficherConseil(mesure);
    } else {
      this.afficherConseil(null);
      this.reprendreMemoire();
    }
  }

  reinitialiser() {
    this.photo = null;
    this.points = [];
    this.champManuel.value = '';
    this.querySelector('[data-file]').value = '';
    this.querySelector('[data-stage]').hidden = true;
    this.afficherConseil(null);
  }

  pointsAToucher() {
    return this.methode === 'bijou' ? TYPES[this.type].bijou : ZONES[this.zone][this.type];
  }

  afficherEtapes() {
    const [a, b] = this.pointsAToucher();
    const etapes = [ETAPE_PHOTO[this.methode], 'Touchez les deux bords de la pièce.', `Touchez ${a}, puis ${b}.`];
    this.querySelector('[data-etapes]').replaceChildren(
      ...etapes.map((texte) => {
        const li = document.createElement('li');
        li.textContent = texte;
        return li;
      })
    );
    this.querySelector('[data-manuel-label]').textContent =
      this.methode === 'bijou'
        ? `Vous connaissez la taille de votre bijou actuel ? ${TYPES[this.type].mot}, en mm`
        : 'Vous avez une règle ? Distance mesurée, en mm';
  }

  ouvrir() {
    this.dialog.showModal();
    if (this.points.length < 4 && !this.champManuel.value) this.reprendreMemoire();
  }

  async chargerPhoto(fichier) {
    if (!fichier) return;
    const url = URL.createObjectURL(fichier);
    try {
      const image = new Image();
      image.src = url;
      await image.decode();
      const ratio = Math.min(1, COTE_MAX_PX / Math.max(image.naturalWidth, image.naturalHeight));
      const photo = document.createElement('canvas');
      photo.width = Math.round(image.naturalWidth * ratio);
      photo.height = Math.round(image.naturalHeight * ratio);
      photo.getContext('2d').drawImage(image, 0, 0, photo.width, photo.height);

      this.photo = photo;
      this.canvas.width = photo.width;
      this.canvas.height = photo.height;
      this.points = [];
      this.champManuel.value = '';
      this.afficherConseil(null);
      this.querySelector('[data-stage]').hidden = false;
      this.mettreAJour();
      this.canvas.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    } catch {
      this.querySelector('[data-stage]').hidden = false;
      this.consigne('Cette photo ne peut pas être ouverte : essayez avec une autre.');
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  // Pixels du canvas par pixel affiché : garde des points et traits de taille constante à l'écran.
  echelle() {
    return this.canvas.width / this.canvas.getBoundingClientRect().width;
  }

  position(event) {
    const cadre = this.canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - cadre.left) * this.canvas.width) / cadre.width,
      y: ((event.clientY - cadre.top) * this.canvas.height) / cadre.height,
    };
  }

  appui(event) {
    if (!this.photo) return;
    event.preventDefault();
    const point = this.position(event);
    // Tant que les 4 points ne sont pas posés, chaque touche en crée un (deux points sont parfois
    // très proches) ; ensuite, on déplace le point le plus proche du doigt.
    let index = -1;
    if (this.points.length < 4) {
      index = this.points.push(point) - 1;
    } else {
      let plusPres = 24 * this.echelle();
      this.points.forEach((p, i) => {
        if (ecart(p, point) < plusPres) [plusPres, index] = [ecart(p, point), i];
      });
      if (index === -1) return;
      this.points[index] = point;
    }
    this.deplacement = index;
    this.canvas.setPointerCapture(event.pointerId);
    this.dessiner();
  }

  glisse(event) {
    if (this.deplacement === null) return;
    this.points[this.deplacement] = this.position(event);
    this.dessiner();
  }

  relache() {
    if (this.deplacement === null) return;
    this.deplacement = null;
    this.mettreAJour();
  }

  mettreAJour() {
    this.dessiner();
    const mesure = this.mesurePhoto();
    if (mesure !== null) {
      this.champManuel.value = '';
      this.afficherConseil(mesure);
    }
  }

  mesurePhoto() {
    if (this.points.length < 4) return null;
    const piece = parseFloat(this.querySelector('[data-coin]').value);
    const pixelsParMm = ecart(this.points[0], this.points[1]) / piece;
    return pixelsParMm > 0 ? ecart(this.points[2], this.points[3]) / pixelsParMm : null;
  }

  distanceManuelle() {
    const mm = parseFloat(this.champManuel.value.replace(',', '.'));
    return Number.isFinite(mm) ? mm : null;
  }

  dessiner() {
    const nombre = this.points.length;
    const [a, b] = this.pointsAToucher();
    const consignes = ['Touchez le bord gauche de la pièce.', 'Touchez le bord droit de la pièce.', `Touchez ${a}.`, `Touchez ${b}.`];
    this.querySelector('[data-undo]').hidden = nombre === 0;
    this.consigne(nombre < 4 ? consignes[nombre] : "C'est mesuré ! Faites glisser un point pour l'ajuster.");
    if (!this.photo) return;

    const { ctx } = this;
    const k = this.echelle();
    ctx.drawImage(this.photo, 0, 0);
    this.trait(0, 1, COULEURS.piece, k);
    this.trait(2, 3, COULEURS.mesure, k);
    this.points.forEach((point, i) => {
      ctx.beginPath();
      ctx.arc(point.x, point.y, 7 * k, 0, Math.PI * 2);
      ctx.fillStyle = i < 2 ? COULEURS.piece : COULEURS.mesure;
      ctx.fill();
      ctx.lineWidth = 2 * k;
      ctx.strokeStyle = '#fff';
      ctx.stroke();
    });

    const mesure = this.mesurePhoto();
    if (mesure !== null) {
      const [p, q] = [this.points[2], this.points[3]];
      this.etiquette(`${enMm(mesure)} mm`, Math.max(p.x, q.x) + 14 * k, (p.y + q.y) / 2, k);
    }
    if (this.deplacement !== null) this.loupe(this.points[this.deplacement], k);
  }

  trait(i, j, couleur, k) {
    const [a, b] = [this.points[i], this.points[j]];
    if (!a || !b) return;
    const { ctx } = this;
    for (const [largeur, teinte] of [[5 * k, '#fff'], [2.5 * k, couleur]]) {
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.lineWidth = largeur;
      ctx.strokeStyle = teinte;
      ctx.stroke();
    }
  }

  etiquette(texte, x, y, k) {
    const { ctx } = this;
    ctx.font = `600 ${15 * k}px system-ui, sans-serif`;
    ctx.textBaseline = 'middle';
    const largeur = ctx.measureText(texte).width;
    const gauche = Math.min(x, this.canvas.width - largeur - 12 * k);
    ctx.fillStyle = 'rgb(255 255 255 / 0.9)';
    ctx.fillRect(gauche - 6 * k, y - 13 * k, largeur + 12 * k, 26 * k);
    ctx.fillStyle = '#22252A';
    ctx.fillText(texte, gauche, y);
  }

  // Loupe ×3 dans le coin opposé au doigt, pour placer le point au millimètre.
  loupe(point, k) {
    const { ctx, canvas } = this;
    const rayon = 56 * k;
    const marge = 12 * k;
    const cx = point.x < canvas.width / 2 ? canvas.width - rayon - marge : rayon + marge;
    const cy = point.y < canvas.height / 2 ? canvas.height - rayon - marge : rayon + marge;

    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, rayon, 0, Math.PI * 2);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.clip();
    ctx.translate(cx, cy);
    ctx.scale(3, 3);
    ctx.translate(-point.x, -point.y);
    ctx.drawImage(this.photo, 0, 0);
    ctx.restore();

    ctx.beginPath();
    ctx.moveTo(cx - 12 * k, cy);
    ctx.lineTo(cx + 12 * k, cy);
    ctx.moveTo(cx, cy - 12 * k);
    ctx.lineTo(cx, cy + 12 * k);
    ctx.lineWidth = 1.5 * k;
    ctx.strokeStyle = '#22252A';
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, rayon, 0, Math.PI * 2);
    ctx.lineWidth = 3 * k;
    ctx.strokeStyle = '#fff';
    ctx.stroke();
  }

  consigne(texte) {
    this.querySelector('[data-instruction]').textContent = texte;
  }

  // Sur soi : la plus petite taille qui laisse le jeu de la zone, puis la suivante.
  // Bijou actuel : la taille la plus proche (à égalité, la plus grande), puis la suivante.
  conseiller(mesure) {
    if (!this.tailles.length) return {};
    if (this.methode === 'bijou') {
      const [conseil] = [...this.tailles].sort((a, b) => Math.abs(a.mm - mesure) - Math.abs(b.mm - mesure) || b.mm - a.mm);
      return { conseil, suivante: this.tailles.find((t) => t.mm > conseil.mm) };
    }
    const marge = ZONES[this.zone]?.marge ?? MARGE_PAR_DEFAUT;
    const possibles = this.tailles.filter((t) => t.mm >= mesure + marge - 0.05);
    return { conseil: possibles[0], suivante: possibles[1], marge };
  }

  afficherConseil(mesure) {
    const zone = this.querySelector('[data-result]');
    if (mesure === null) {
      zone.hidden = true;
      zone.replaceChildren();
      return;
    }
    zone.hidden = false;

    const type = TYPES[this.type];
    if (mesure < type.min || mesure > type.max) {
      zone.innerHTML = `<p>Mesure inhabituelle (${enMm(mesure)} mm) : vérifiez la pièce choisie et la place des points.</p>`;
      return;
    }

    this.memoriser(mesure);
    const { conseil, suivante, marge } = this.conseiller(mesure);
    const lien = `<a href="${echapper(this.dataset.contact)}">Envoyez-nous une photo, on vous conseille !</a>`;
    const intitule = this.methode === 'bijou' ? 'Votre bijou actuel' : 'Distance mesurée';
    let html = `<p>${intitule} : <strong>${enMm(mesure)} mm</strong></p>`;
    if (!this.tailles.length) {
      const besoin = this.methode === 'bijou' ? mesure : mesure + (ZONES[this.zone]?.marge ?? MARGE_PAR_DEFAUT);
      const mot = `${type.mot.toLowerCase()} conseillé${type.accord}`;
      zone.innerHTML = `${html}<p>${mot.charAt(0).toUpperCase() + mot.slice(1)} : <strong>${type.prefixe}${enMm(Math.ceil(besoin))} mm</strong> environ. ${lien}</p>`;
      return;
    }
    if (!conseil && !this.unique) {
      zone.innerHTML = `${html}<p>Les tailles de ce bijou sont un peu petites pour votre piercing. ${lien}</p>`;
      return;
    }

    const taille = (t) => `${type.prefixe}${echapper(t.label)}`;
    if (this.unique) {
      zone.innerHTML = `${html}${this.verdictUnique(mesure, conseil, taille, lien)}`;
      return;
    }
    let precision;
    if (this.methode === 'bijou') {
      precision = Math.abs(conseil.mm - mesure) <= 0.75 ? 'comme votre bijou actuel' : 'la plus proche de votre bijou actuel';
    } else if (this.type === 'anneau') {
      precision = conseil.mm - mesure <= marge + 2 ? 'porté ajusté' : 'porté plus ample';
    } else {
      precision = conseil.mm - mesure <= marge + 2 ? 'bien ajustée' : 'avec un peu de jeu';
    }
    html += `<p>${type.mot} conseillé${type.accord} : <strong>${taille(conseil)}</strong>, ${precision}</p>`;
    if (suivante) {
      html += `<p>${this.type === 'anneau' ? 'Pour un rendu plus ample' : 'Pour plus de jeu'} : ${taille(suivante)}</p>`;
    }
    if (this.methode === 'bijou' && Math.abs(conseil.mm - mesure) > 1.5) {
      html += `<p>Aucune taille ne correspond exactement à votre bijou. ${lien}</p>`;
    }
    html += `<button type="button" class="button" data-apply>Choisir ${taille(conseil)}</button>`;
    zone.innerHTML = html;
    zone.querySelector('[data-apply]').addEventListener('click', () => this.choisir(conseil, mesure));
  }

  // Bijou en taille unique : convient-il à la mesure ?
  verdictUnique(mesure, conseil, taille, lien) {
    const [bijou] = this.tailles;
    const nom = `<strong>${taille(bijou)}</strong>`;
    if (this.methode === 'bijou') {
      const diff = bijou.mm - mesure;
      if (Math.abs(diff) <= 0.75) return `<p>✓ Ce bijou fait ${nom}, comme votre bijou actuel : il vous convient.</p>`;
      return `<p>Ce bijou fait ${nom}, ${diff > 0 ? 'un peu plus grand' : 'un peu plus petit'} que votre bijou actuel. ${lien}</p>`;
    }
    if (conseil) return `<p>✓ Ce bijou fait ${nom} : il vous convient.</p>`;
    return `<p>Ce bijou fait ${nom} : il risque d'être un peu petit pour votre piercing. ${lien}</p>`;
  }

  // Sélectionne la taille dans le sélecteur de variantes de la fiche.
  choisir(taille, mesure) {
    const id = this.dataset.productId;
    const picker =
      document.querySelector(`variant-picker[data-product-id="${id}"][data-template-product-match="true"]`) ||
      document.querySelector(`variant-picker[data-product-id="${id}"]`);
    const bouton = picker
      ? [...picker.querySelectorAll(`input[data-input-id^="${this.dataset.optionPosition}-"]`)].find(
          (input) => input.value === taille.label
        )
      : null;
    const liste = picker?.querySelector(`select[name="options[${CSS.escape(this.dataset.optionName)}]"]`);

    this.dialog.close();
    if (bouton && !bouton.checked) {
      bouton.checked = true;
      bouton.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (!bouton && liste && liste.value !== taille.label) {
      liste.value = taille.label;
      liste.dispatchEvent(new Event('change', { bubbles: true }));
    }
    picker?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    window.Shopify?.analytics?.publish?.('mesureur_taille', {
      type: this.type,
      methode: this.methode,
      zone: this.zone,
      mesure_mm: Math.round(mesure * 10) / 10,
      taille: taille.label,
    });
  }

  // Les mesures sont gardées par type de bijou, méthode et zone, pour les autres fiches.
  cleMemoire() {
    return [this.type, this.methode, this.zone ?? ''].join('|');
  }

  lireMemoire() {
    try {
      const mesure = JSON.parse(localStorage.getItem(CLE_MEMOIRE) || '{}')[this.cleMemoire()];
      return mesure && Date.now() - mesure.date < DUREE_MEMOIRE ? mesure : null;
    } catch {
      return null;
    }
  }

  memoriser(valeur) {
    try {
      const memoire = JSON.parse(localStorage.getItem(CLE_MEMOIRE) || '{}');
      memoire[this.cleMemoire()] = { valeur: Math.round(valeur * 10) / 10, date: Date.now() };
      localStorage.setItem(CLE_MEMOIRE, JSON.stringify(memoire));
    } catch {
      // Navigation privée : la mesure n'est simplement pas gardée.
    }
    this.afficherMemoire();
  }

  reprendreMemoire() {
    const mesure = this.lireMemoire();
    if (!mesure) return;
    this.champManuel.value = enMm(mesure.valeur);
    this.afficherConseil(mesure.valeur);
  }

  afficherMemoire() {
    const ligne = this.querySelector('[data-memo]');
    const mesure = this.lireMemoire();
    if (!mesure) return;
    const { conseil } = this.conseiller(mesure.valeur);
    const ou = this.methode === 'bijou' ? 'votre bijou actuel' : ZONES[this.zone].label.toLowerCase();
    const suite = this.unique
      ? conseil ? ' · ce bijou vous convient' : ''
      : conseil ? ` · ${TYPES[this.type].prefixe}${conseil.label} conseillé sur ce bijou` : '';
    ligne.textContent = `Votre mesure (${ou}) : ${enMm(mesure.valeur)} mm${suite}`;
    ligne.hidden = false;
  }
}

if (!customElements.get('nose-sizer')) customElements.define('nose-sizer', NoseSizer);
